import {
  BulkActionContainerCol,
  Col,
  Table,
  TableActionBarRow,
  ValueOrFallback,
} from "melco-ui";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  BulkActionCount,
  FormattedDate,
  FusionMelcoModelsUserAlphabet,
  Search,
  useTableSelection,
  TableCellLink,
} from "melco-shared-logic";
import { useFonts } from "../../../hooks/libraries/fonts/useFonts";
import { BulkActionDeleteFonts } from "./BulkActionDeleteFonts";
import { PublishedState } from "../../state/PublishedState";

const DETAIL_VIEW_PATH = "/libraries/fonts/embroidery";

export const FontsList: React.FC = () => {
  const {
    isLoading,
    isFetching,
    data,
    isPreviousData,
    errorComponent,
    onTableConfigChange,
    pagination,
    searchQueryChange,
    refetch,
  } = useFonts("embroidery");
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { selectedRowKeys, setSelectedRowKeys, rowSelection, onRow } =
    useTableSelection((row: FusionMelcoModelsUserAlphabet) => {
      navigate([DETAIL_VIEW_PATH, row.id].join("/"));
    });

  const columns = [
    {
      title: t("fonts_list.columns.name"),
      key: "name",
      dataIndex: "name",
      sorter: true,
      render: (_: string, item: FusionMelcoModelsUserAlphabet) => (
        <ValueOrFallback
          value={item.name}
          render={(value) => (
            <TableCellLink to={[DETAIL_VIEW_PATH, item.id].join("/")}>
              {value}
            </TableCellLink>
          )}
          fallback={t("global.fallback.empty")}
        />
      ),
    },
    {
      title: t("fonts_list.columns.type"),
      key: "type",
      dataIndex: "type",
      sorter: true,
    },
    {
      title: t("fonts_list.columns.created_date"),
      key: "created_date",
      dataIndex: "created_date",
      sorter: true,
      render: (_: string, item: FusionMelcoModelsUserAlphabet) => (
        <FormattedDate date={item.created_date} />
      ),
    },
    {
      title: t("fonts_list.columns.updated_date"),
      key: "updated_date",
      dataIndex: "updated_date",
      sorter: true,
      render: (_: string, item: FusionMelcoModelsUserAlphabet) => (
        <FormattedDate date={item.updated_date} />
      ),
    },
    {
      title: t("fonts_list.columns.publish_state"),
      key: "publish_state",
      dataIndex: "publish_state",
      sorter: true,
      render: (_: string, item: FusionMelcoModelsUserAlphabet) => {
        return <PublishedState publishedState={item.publish_state!} />;
      },
    },
  ];

  return (
    <>
      <TableActionBarRow>
        <Col>
          <Search
            onSearchInputChange={(query: string) => searchQueryChange(query)}
          />
        </Col>
        <BulkActionContainerCol>
          <BulkActionCount selectedCount={selectedRowKeys.length} />
          <BulkActionDeleteFonts
            selectedRowKeys={selectedRowKeys}
            setSelectedRowKeys={setSelectedRowKeys}
            refetch={refetch}
            data={data}
          />
        </BulkActionContainerCol>
      </TableActionBarRow>
      <Table
        showSorterTooltip={false}
        sticky
        loading={isPreviousData ? isFetching : isLoading}
        dataSource={data}
        columns={columns}
        onChange={onTableConfigChange}
        pagination={pagination}
        rowKey="id"
        rowSelection={rowSelection}
        onRow={onRow}
        rowClassName={() => "clickable"}
        noDataMessage={t("fonts_list.empty")}
        errorComponent={errorComponent}
      />
    </>
  );
};
